import { ApolloCache } from '@apollo/client';
import { ThemeTypographyColor } from 'DesignSystem/utils/designSystemUtils';
import { TCompanyStatusType, ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import { GET_PARTNER_USER_HOME_DATA, GET_PRODUCTS_HOME } from 'graphql/secure/queries';
import {
  GET_ALL_PARTNER_COMPANIES,
  GET_PARTNER_OVERVIEW,
  IGetPartnerUserHomeDataValues,
} from 'graphql/secure/queries/partner.queries';
import {
  IBasePartnerCompany,
  IPartnerCompany,
  IPartnerRequest,
} from 'FeatureModules/Partner/types/partner.types';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';
import { IGraphQlConnection } from 'types/core.types';

export const addNewPartnerToCache = (
  cache: ApolloCache<unknown>,
  newPartner: IBasePartnerCompany
) => {
  const result = cache.readQuery<{ partners: IGraphQlConnection<IBasePartnerCompany> }>({
    query: GET_ALL_PARTNER_COMPANIES,
  });

  const partners = result?.partners || { edges: [] };

  cache.writeQuery({
    query: GET_ALL_PARTNER_COMPANIES,
    data: {
      partners: {
        edges: [
          {
            node: newPartner,
            __typename: 'PartnerEdge',
          },
          ...partners.edges,
        ],
      },
    },
  });
};

export const removeRequestFromCachePartnerHome = (
  cache: ApolloCache<unknown>,
  removedRequest: IPartnerRequest
) => {
  const cachedData = cache.readQuery<IGetPartnerUserHomeDataValues>({
    query: GET_PARTNER_USER_HOME_DATA,
    variables: {
      filters: {
        requestStatus: ['REQUESTED'],
      },
    },
  });
  const updatedRequests = cachedData?.partnerRequests?.edges.filter(
    request => removedRequest.id !== request.node.id
  );

  cache.writeQuery({
    query: GET_PARTNER_USER_HOME_DATA,
    data: {
      documents: cachedData?.documents,
      partnerRequests: { edges: updatedRequests },
    },
    variables: {
      filters: {
        requestStatus: ['REQUESTED'],
      },
    },
  });
};

export const addRequestToCachePartnerOverview = (
  cache: ApolloCache<unknown>,
  request: IPartnerRequest,
  companyId: string
) => {
  const cachedData = cache.readQuery<
    {
      partner: IPartnerCompany;
    },
    {
      id: string;
    }
  >({
    query: GET_PARTNER_OVERVIEW,
    variables: {
      id: companyId,
    },
  });

  if (!cachedData) return;

  const updatedRequests = [...cachedData.partner.requests.edges, { node: request }];

  cache.writeQuery({
    query: GET_PARTNER_OVERVIEW,
    data: {
      partner: { ...cachedData?.partner, requests: { edges: updatedRequests } },
    },
    variables: {
      id: companyId,
    },
  });
};

export const removeRequestFromCacheBuyerHome = (
  cache: ApolloCache<unknown>,
  removedRequest: IPartnerRequest
) => {
  const cachedData = cache.readQuery<{
    products: IGraphQlConnection<IProduct>;
    overallProductStatus: { status: string; homeTitle: string };
    companyAnalyticsExist: boolean;
    partnerRequests: IGraphQlConnection<IPartnerRequest>;
  }>({
    query: GET_PRODUCTS_HOME,
    variables: {
      filters: {
        requestStatus: ['DENIED', 'PROVIDED'],
      },
    },
  });

  if (!cachedData) return;

  const updatedRequests = cachedData.partnerRequests?.edges.filter(
    request => removedRequest.id !== request.node.id
  );

  cache.writeQuery({
    query: GET_PRODUCTS_HOME,
    data: {
      ...cachedData,
      partnerRequests: { edges: updatedRequests },
    },
    variables: {
      filters: {
        requestStatus: ['DENIED', 'PROVIDED'],
      },
    },
  });
};
export const getReadableCompanyStatus = (status: TCompanyStatusType): string => {
  switch (status) {
    case ECompanyStatus.INVITED:
      return 'INVITATION SENT';
    case ECompanyStatus.NOT_INVITED:
      return 'NOT INVITED';
    case ECompanyStatus.REGISTERED:
      return 'ACTIVE COLLABORATOR';
    default:
      return '';
  }
};

export const getCompanyStatusColor = (status: TCompanyStatusType): ThemeTypographyColor => {
  switch (status) {
    case ECompanyStatus.INVITED:
      return 'YELLOW';
    case ECompanyStatus.NOT_INVITED:
      return 'GRAY';
    case ECompanyStatus.REGISTERED:
      return 'GREEN';
    default:
      return 'GRAY';
  }
};
